import type { MeetingPrivate, MeetingPublic } from '@repo/types/meetings';

import { format, parseISO } from 'date-fns';
import { BuildingIcon, ClockIcon, Link2Icon } from 'lucide-react';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Badge } from '@/components/ui/badge';

/**
 * Props for the MeetingCard component.
 * @property {MeetingPrivate | MeetingPublic} meeting - The meeting data to display.
 * @property {...ComponentProps<typeof Link>} [props] - Additional props passed to the Link component.
 */
interface MeetingCardProps extends Omit<ComponentProps<typeof Link>, 'to'> {
  meeting: MeetingPrivate | MeetingPublic;
}

export default function MeetingCard(meetingCardProps: MeetingCardProps) {
  const { meeting, ...props } = meetingCardProps;
  const { id, name, startDate, endDate, meetingURL, isCancelled } = meeting;
  const [isCurrent, setIsCurrent] = useState<boolean>(false);
  const navigate = useNavigate();

  const date = useMemo(() => format(parseISO(startDate), 'd'), [startDate]);
  const month = useMemo(() => format(parseISO(startDate), 'MMM'), [startDate]);

  const timeRange = useMemo(() => format(parseISO(startDate), 'H:mm') + ' - ' + format(parseISO(endDate), 'H:mm'), [endDate, startDate]);

  const currentMeetingCb = useCallback(() => {
    const now = new Date();
    if (now >= parseISO(startDate) && now <= parseISO(endDate) && !isCancelled) {
      setIsCurrent(true);
    } else {
      setIsCurrent(false);
    }
  }, [endDate, startDate, isCancelled]);

  useEffect(() => {
    currentMeetingCb();
    const interval = setInterval(currentMeetingCb, 15000);

    return () => clearInterval(interval);
  }, [currentMeetingCb]);

  return (
    <Link
      {...props}
      to={`/calendar/meeting/${id}`}
      aria-label={'meeting-card'}
      aria-disabled={isCancelled}
      data-current={isCurrent}
      data-state={!isCancelled && isCurrent}
      className={
        'group transition-outline outline-focus-ring bg-card/75 text-foreground border-border-primary hover:bg-accent/80 active:bg-accent data-[state=true]:border-border-brand-accent relative inline-flex w-full shrink-0 cursor-pointer gap-2 overflow-hidden rounded-md border p-2 opacity-100 outline-0 transition-colors duration-150 focus-visible:outline-3 disabled:pointer-events-none disabled:opacity-60 aria-disabled:cursor-not-allowed'
      }
    >
      <div className={'bg-muted group-data-[current=true]:bg-brand-accent-focus flex size-11.5 flex-col items-center justify-between rounded-sm p-0.5'}>
        <p className={'text-muted-foreground group-data-[current=true]:text-brand text-xs'}>{month}</p>
        <div className={'bg-accent dark:bg-card group-data-[current=true]:bg-brand-accent flex w-full justify-center rounded-[6px] p-0.5'}>
          <p className={'text-muted-foreground group-data-[current=true]:text-brand text-sm font-semibold'}>{date}</p>
        </div>
      </div>
      <div className={'h-full space-y-0.5'}>
        <div className={'flex gap-1'}>
          <span className={'bg-brand aria-disabled:bg-destructive my-0.5 w-1 min-w-1 rounded-sm'} />
          <p className={'text-accent-foreground truncate text-start text-sm font-medium'}>{name}</p>
        </div>
        <div className={'flex flex-nowrap gap-1 overflow-hidden'}>
          <Badge size={'sm'} variant={'muted'} className={'aria-disabled:line-through'}>
            <ClockIcon />
            {timeRange}
          </Badge>
          {'location' in meeting && meeting.location ? (
            <Badge size={'sm'} variant={'muted'} className={'aria-disabled:line-through'}>
              <BuildingIcon />
              {meeting.location.name}
            </Badge>
          ) : null}
          {meetingURL ? (
            <Badge size={'sm'} variant={'brand'} className={'aria-disabled:line-through'} onClick={() => navigate(meetingURL)}>
              <Link2Icon />
              Online
            </Badge>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
